import {useState, useMemo} from 'react';
import {Box} from '@mui/material';
import {useLocation} from 'react-router-dom';
import {t} from 'i18next';

import JobResultHeader from './job-result-header/JobResultHeader';
import JobResultTabs, {TabConfig} from './job-result-tabs/JobResultTabs';
import ArrAccordion from '../../../common/arr-accordion/ArrAccordion';
import ArrReviewMetrics from '../../../common/arr-review-metrics/ArrReviewMetrics';
import {JobDetailsTab} from '../common/JobDetails/JobDetailsTab';
import GroundTruthEvaluation from '../ground-truth-evaluation/GroundTruthEvaluation';
import IssueCodeDataGrid from './data-grid/IssueCodeDataGrid';
import './JobResults.scss';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import DocErrorPanel from './job-error-panel/DocErrorPanel';
import {useGetAIReviewJobResult} from '../../../../hooks/ai/review/useReview';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

//   TabPanel defined outside to prevent re-mounting on every render

const TabPanel = ({children, value, index, ...other}: TabPanelProps) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`job-result-tabpanel-${index}`}
    className={`tabpanel-${value === index ? 'active' : 'hidden'}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </Box>
);

const JobResults = () => {
  const {state} = useLocation();
  const [tab, setTab] = useState(0);

  const [isDocErrorPanelOpen, setIsDocErrorPanelOpen] = useState(false);
  const {data: reviewJobResult} = useGetAIReviewJobResult({
    jobId: 2, //TODO: passing hardcoded jobID as 2, need to replace with dynamic jobId in future/
  });

  const handleDocErrorPanelOpen = () => {
    setIsDocErrorPanelOpen(!isDocErrorPanelOpen);
  };

  // This is where you manage tab visibility and content in one place.

  const activeTabs = useMemo<TabConfig[]>(() => {
    const jobType = state?.jobType;
    const isIssueCoding = jobType === t('ai.review.jobType.issueCoding');

    const config = [
      {
        label: t('ai.review.jobResult.tabs.results'),
        component: (
          <ArrReviewMetrics
            type={jobType}
            handleDocErrorPanelOpen={handleDocErrorPanelOpen}
            metricsDetails={reviewJobResult}
          />
        ),
      },
      {
        label: t('ai.review.jobResult.tabs.jobDetails'),
        component: <JobDetailsTab jobId={'2'} />, // TODO: passing hardcoded jobID as 2, need to replace with dynamic jobId in future/
      },
      {
        label: t('ai.review.jobResult.tabs.groundTruthEvaluation'),
        component: <GroundTruthEvaluation jobId={state.jobId} />,
        // Per original logic: hide if issueCoding, otherwise show but disable
        hidden: isIssueCoding,
        // disabled: true,
      },
    ];

    return config.filter((t) => !t.hidden);
  }, [state?.jobType, t, reviewJobResult]);

  // Looping through activeTabs for both the Header (Tabs) and Content (Panels)

  const sections = useMemo(
    () => [
      {
        id: 'results',
        titleTabIndex: -1,
        summaryTabIndex: -1,
        expandIconRole: 'presentation',
        expandIconAriaHidden: true,
        summaryRole: 'presentation',
        SummaryComponent: 'div',
        title: (
          <JobResultTabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            tabs={activeTabs}
          />
        ),
        content: (
          <Box className="tab-panels-wrapper">
            {activeTabs.map((item, index) => (
              <TabPanel key={`panel-${index}`} value={tab} index={index}>
                {item.component}
              </TabPanel>
            ))}
          </Box>
        ),
      },
    ],
    [tab, activeTabs]
  );

  return (
    <Box className="job-result-container">
      <JobResultHeader />
      <Box className="tab-container">
        <ArrAccordion
          sections={sections}
          expandedSection="results"
          className="accordion-container"
          dataTestId="job-results"
          headingLevel="p"
        />
      </Box>
      <ArrDrawer
        isOpen={isDocErrorPanelOpen}
        onClose={handleDocErrorPanelOpen}
        ariaLabel="drawer content"
        size="large"
        side="right"
        showButton={true}
        showHeader={true}
        className="doc-error-panel"
      >
        <DocErrorPanel />
      </ArrDrawer>

      <IssueCodeDataGrid jobId={state?.jobId} type={state?.jobType} />
    </Box>
  );
};

export default JobResults;
